import { Character, CharacterSprite, CharacterAnimator, CharacterMetadata, OptimizedAtlasLoader, CharacterShadow } from '../character/index.js';
import LPCCompositor from '../character/LPCCompositor.js';
import { buildLayerStack, loadManifest } from '../character/LPCLayerDefinitions.js';
import CombatStats from '../combat/CombatStats.js';
import Debug from '../utils/Debug.js';
import ProfanityFilter from '../utils/ProfanityFilter.js';

const DEFAULT_SPRITE_OFFSET = { x: 0, y: -0.88, z: 0, scale: 1.4 };

// Peer colors for nametags — must match PlayerChat.PEER_COLORS
const PEER_COLORS = [
  '#f0a030', '#e06060', '#a080e0', '#e07080',
  '#c0a060', '#e0a0c0', '#d08040', '#c070d0',
];

const CHARACTER_CONFIGS = {
  boy: { idle: 'fight-stance-idle-8-frames', walk: 'walking-8-frames', run: 'running-8-frames', spriteOffset: DEFAULT_SPRITE_OFFSET },
  mage: { idle: 'breathing-idle', walk: 'walking-8-frames', run: 'running-8-frames', spriteOffset: DEFAULT_SPRITE_OFFSET },
  merchant: { idle: 'breathing-idle', walk: 'walking-8-frames', run: 'running-8-frames', spriteOffset: DEFAULT_SPRITE_OFFSET },
  surfer: { idle: 'breathing-idle', walk: 'walking-8-frames', run: 'running-8-frames', spriteOffset: DEFAULT_SPRITE_OFFSET },
  bear: { idle: 'idle-long', walk: 'walk-6-frames', run: 'running-6-frames', spriteOffset: { x: 0, y: -1.12, z: 0, scale: 1.4 } },
  hellrat: { idle: 'idle', walk: 'walk-6-frames', run: 'running-6-frames', spriteOffset: { x: 0, y: -1.42, z: 0, scale: 1.4 } },
  rogue: { idle: 'fight-stance-idle-8-frames', walk: 'walking-8-frames', run: 'running-8-frames', spriteOffset: DEFAULT_SPRITE_OFFSET }
};

// LPC-based characters use the same animation names as boy/mage
const LPC_SPRITE_OFFSET = { x: 0, y: -0.6, z: 0, scale: 1.4 };
const LPC_CONFIG = { idle: 'breathing-idle', walk: 'walking-8-frames', run: 'running-8-frames', spriteOffset: LPC_SPRITE_OFFSET };

/**
 * Synchronizes player characters between peers.
 * Broadcasts local player state at 10Hz and renders remote player characters.
 * Also handles instant action events (jumps, attacks, fireballs).
 */
export default class PlayerSync {
  static BROADCAST_INTERVAL_MS = 100; // 10Hz
  static INTERPOLATION_SPEED = 10;
  static MAX_VELOCITY = 15;         // Cap velocity to prevent overshoot from burst updates
  static HEARTBEAT_INTERVAL_MS = 2000; // Send heartbeat even when idle
  static STALE_THRESHOLD_MS = 3000;    // Consider remote player stale after 3s without updates

  /**
   * @param {World} world
   * @param {NetworkManager} networkManager
   * @param {Character} localPlayer
   */
  constructor(world, networkManager, localPlayer) {
    this.world = world;
    this.network = networkManager;
    this.localPlayer = localPlayer;
    this.remotePlayers = new Map(); // peerId -> { character, targetState, prevTarget, velocity, lastUpdateTime, config }
    this._broadcastTimer = 0;
    this._atlasCache = new Map();
    this._creatingPeers = new Set(); // peers currently being created (prevents concurrent creation)
    this._lastHelloResponse = 0; // throttle hello responses to prevent flooding
    this._lastBroadcastState = null; // last broadcast state for idle detection
    this._lastBroadcastTime = 0;     // timestamp of last broadcast
    this._lastInstLevelKey = null;   // tracks instance+level for visibility re-evaluation

    // Set by main.js for instance-aware filtering (survives dungeon transitions)
    this._dungeonInstanceManager = null;

    // Nickname system — broadcast with player state, displayed as floating nametag
    this.localNickname = null;      // Raw nickname from settings
    this.localDisplayNick = null;   // Deduplicated display name (with #2 suffix if needed)
    this._roomJoinTime = 0;         // Timestamp when we joined the room (for dedup priority)

    // Local equipped item definitions — set by main.js on inventory change
    // so broadcasts include lpcLayer data for remote peers to render gear.
    this._localEquipment = null;

    // Callback for remote fireball events — wired by main.js to ProjectileManager
    this.onRemoteFireball = null;

    // Callback when local display nick changes due to dedup
    this.onDisplayNickChange = null;

    this.network.onPlayerUpdate = (data, peerId) => this._onRemoteUpdate(data, peerId);
    this.network.onAction = (data, peerId) => this._onRemoteAction(data, peerId);
  }

  /**
   * Broadcast an instant action event to all peers.
   * @param {Object} data - { type: 'anim'|'fireball', ... }
   */
  broadcastAction(data) {
    if (this.network.isConnected) {
      this.network.broadcastAction(data);
    }
  }

  /**
   * Called every frame from the game loop.
   * Handles broadcasting local state and interpolating remote players.
   * @param {number} deltaTime - Seconds since last frame
   */
  update(deltaTime) {
    // Broadcast local player state at 10Hz
    this._broadcastTimer += deltaTime * 1000;
    if (this._broadcastTimer >= PlayerSync.BROADCAST_INTERVAL_MS) {
      this._broadcastTimer = 0;
      this._broadcastLocalState();
    }

    // Re-evaluate cross-instance/level visibility when local instance or level changes
    const dim = this._dungeonInstanceManager;
    const myInst = dim?._isInDungeon ? dim._instanceId : null;
    const myLevel = dim?._isInDungeon ? dim.currentLevel : undefined;
    const instLevelKey = `${myInst}:${myLevel}`;
    if (instLevelKey !== this._lastInstLevelKey) {
      this._lastInstLevelKey = instLevelKey;
      for (const [peerId, remote] of this.remotePlayers) {
        if (!remote.character) continue;
        const theirInst = dim?._peerInstances.get(peerId) || null;
        const theirLevel = dim?._peerLevels.get(peerId);
        const visible = (myInst === theirInst) && (myInst === null || theirLevel === undefined || theirLevel === myLevel);
        // Use visibility:hidden (not display:none) — the World.js render loop
        // forces display:'' on all character sprites every frame, which overrides
        // display:none. visibility:hidden is not touched by the render loop.
        if (remote.character.sprite?.element) {
          remote.character.sprite.element.style.visibility = visible ? '' : 'hidden';
        }
        if (remote.character.shadow?.element) {
          remote.character.shadow.element.style.visibility = visible ? '' : 'hidden';
        }
      }
    }

    // Interpolate remote players toward their target positions
    const now = Date.now();
    for (const [peerId, remote] of this.remotePlayers) {
      if (!remote.character || !remote.targetState) continue;

      // Skip interpolation for players in different instance or dungeon level.
      // Dual check: DIM tracking AND broadcast data (race condition protection).
      const dimInst = dim?._peerInstances.get(peerId) || null;
      if (myInst !== dimInst) continue;
      const dimLevel = dim?._peerLevels.get(peerId);
      if (myInst !== null && dimLevel !== undefined && dimLevel !== myLevel) continue;
      // Also check broadcast data
      if (myInst !== null) {
        const bInst = remote.targetState.inst || null;
        if (myInst !== bInst) continue;
        if (remote.targetState.lvl !== undefined && remote.targetState.lvl !== myLevel) continue;
      }

      const char = remote.character;
      const target = remote.targetState;
      const config = remote.config;
      const timeSinceUpdate = now - remote.lastUpdateTime;

      // Staleness detection: if no updates for 3+ seconds, force idle.
      // This prevents characters from appearing to walk/slide forever
      // when the WebRTC data channel degrades or disconnects silently.
      if (timeSinceUpdate > PlayerSync.STALE_THRESHOLD_MS) {
        if (config) {
          const animator = char.animator;
          if (animator && !animator.isPlayingAction && animator.currentState !== config.idle) {
            animator.setState(config.idle);
          }
        }
        remote.velocity = null;
        continue; // Skip interpolation — hold at last known position
      }

      // Velocity-predicted interpolation:
      // Extrapolate where the player should be based on velocity,
      // then lerp toward that prediction for smooth motion.
      let predictX = target.x;
      let predictY = target.y;
      let predictZ = target.z;

      if (remote.velocity && target.isMoving) {
        const tSinceUpdate = timeSinceUpdate / 1000;
        // Only predict up to 200ms ahead to prevent overshoot
        const predictTime = Math.min(tSinceUpdate, 0.2);
        predictX += remote.velocity.x * predictTime;
        predictY += remote.velocity.y * predictTime;
        predictZ += remote.velocity.z * predictTime;
      }

      const t = Math.min(PlayerSync.INTERPOLATION_SPEED * deltaTime, 1);
      char.x += (predictX - char.x) * t;
      char.y += (predictY - char.y) * t;
      char.z += (predictZ - char.z) * t;

      // Snap facing angle
      char.facingAngle = target.angle;

      // Update animation state (walk/run/idle only — actions are handled via _onRemoteAction)
      if (config) {
        // Don't override action animations (jump, attack) — let them finish
        const animator = char.animator;
        if (animator && !animator.isPlayingAction) {
          if (target.isMoving) {
            const animName = target.isRunning ? config.run : config.walk;
            if (animator.currentState !== animName) {
              animator.setState(animName);
            }
          } else {
            if (animator.currentState !== config.idle) {
              animator.setState(config.idle);
            }
          }
        }
      }

    }
  }

  /**
   * @param {boolean} force - If true, bypass idle optimization (used by broadcastImmediately,
   *   hello responses, and peer join retries where the broadcast MUST go through).
   */
  _broadcastLocalState(force = false) {
    if (!this.localPlayer || !this.network.isConnected) return;

    const p = this.localPlayer;
    const nick = this.localDisplayNick || this.localNickname;
    const state = {
      x: p.x, y: p.y, z: p.z,
      angle: p.facingAngle,
      isMoving: p.isMoving,
      isRunning: p.isRunning,
      characterName: p.characterName || 'boy',
      nick: nick || undefined,  // omitted if null — saves bandwidth
      jt: this._roomJoinTime || undefined  // join timestamp for dedup priority
    };

    // Include LPC appearance + equipment only when the atlas is actually LPC.
    // On initial load with saved appearance, the boy atlas shows while LPC
    // composition is async — don't broadcast lpcA until the LPC atlas is ready.
    if (p.sprite?.atlas?._isLPC && p.sprite.getLPCAppearance()) {
      state.lpcA = p.sprite.getLPCAppearance();
      // Include equipped item lpcLayer data so remote peers render gear
      if (this._localEquipment) {
        const eq = {};
        for (const [slot, def] of Object.entries(this._localEquipment)) {
          if (def?.lpcLayer) {
            eq[slot] = def.lpcLayer; // { layerId, variant }
          }
        }
        if (Object.keys(eq).length > 0) state.lpcE = eq;
      }
    }

    // Include dungeon instance + level for cross-instance filtering
    if (this._dungeonInstanceManager?._isInDungeon) {
      state.lvl = this._dungeonInstanceManager.currentLevel;
      state.inst = this._dungeonInstanceManager._instanceId;
    }

    // Skip broadcast if nothing changed — reduces bandwidth for idle players.
    // force=true bypasses this for critical broadcasts (peer discovery, hello responses).
    if (!force) {
      const last = this._lastBroadcastState;
      if (last) {
        const dx = state.x - last.x;
        const dy = state.y - last.y;
        const dz = state.z - last.z;
        const moved = dx * dx + dy * dy + dz * dz > 0.0001;
        const angleDelta = Math.abs(state.angle - last.angle);
        const turned = angleDelta > 0.05;
        const stateChanged = state.isMoving !== last.isMoving || state.isRunning !== last.isRunning;
        const lpcChanged = !!state.lpcA !== !!last.lpcA;
        const now = Date.now();
        const heartbeatDue = (now - this._lastBroadcastTime) > PlayerSync.HEARTBEAT_INTERVAL_MS;

        if (!moved && !turned && !stateChanged && !lpcChanged && !heartbeatDue) return;
      }
    }

    this._lastBroadcastState = state;
    this._lastBroadcastTime = Date.now();
    this.network.broadcastPlayerState(state, force);
  }

  async _onRemoteUpdate(data, peerId) {
    let remote = this.remotePlayers.get(peerId);

    if (!remote) {
      // First update from this peer — create their character
      remote = {
        character: null,
        targetState: data,
        prevTarget: null,
        velocity: null,
        lastUpdateTime: Date.now(),
        config: null,
        nickname: data.nick || null,
        joinTime: data.jt || 0,
        nametagEl: null
      };
      this.remotePlayers.set(peerId, remote);
      // New peer arrived — re-check dedup in case they have the same nickname
      if (remote.nickname) this._recheckLocalDedup();
      await this._createRemoteCharacter(peerId, data);
      return;
    }

    // Compute velocity from position delta between updates.
    // Cap velocity to prevent wild overshoot when buffered messages arrive in bursts
    // (small dt between consecutive queued messages = enormous velocity values).
    const now = Date.now();
    const dt = (now - remote.lastUpdateTime) / 1000;
    if (dt > 0.01 && remote.targetState) {
      const cap = PlayerSync.MAX_VELOCITY;
      remote.velocity = {
        x: Math.max(-cap, Math.min(cap, (data.x - remote.targetState.x) / dt)),
        y: Math.max(-cap, Math.min(cap, (data.y - remote.targetState.y) / dt)),
        z: Math.max(-cap, Math.min(cap, (data.z - remote.targetState.z) / dt))
      };
    }

    remote.prevTarget = remote.targetState;
    remote.targetState = data;
    remote.lastUpdateTime = now;

    // Update join time (first broadcast may arrive before peer finishes joining)
    if (data.jt && (!remote.joinTime || data.jt < remote.joinTime)) {
      remote.joinTime = data.jt;
    }

    // Update nickname and nametag text
    const newNick = data.nick || null;
    if (newNick !== remote.nickname) {
      remote.nickname = newNick;
      if (remote.nametagEl) {
        remote.nametagEl.textContent = ProfanityFilter.filter(newNick || this._titleCase(data.characterName || 'player'));
      }
      // Re-check if our local nickname now has a duplicate
      this._recheckLocalDedup();
    }

    // Cross-instance/level visibility: hide characters in different instance or level.
    // Check BOTH DIM tracking AND broadcast data — the action channel (instance-change)
    // and player channel (broadcasts) can arrive out of order, so either source may
    // have the newer level. If EITHER says different level → hide.
    if (remote.character) {
      const dim = this._dungeonInstanceManager;
      const myInst = dim?._isInDungeon ? dim._instanceId : null;
      const myLevel = dim?._isInDungeon ? dim.currentLevel : undefined;
      let visible = true;
      // Check DIM tracking
      const dimInst = dim?._peerInstances.get(peerId) || null;
      const dimLevel = dim?._peerLevels.get(peerId);
      if (myInst !== dimInst) {
        visible = false;
      } else if (myInst !== null && dimLevel !== undefined && dimLevel !== myLevel) {
        visible = false;
      }
      // Also check broadcast data (catches race where broadcast arrives before action)
      if (visible && myInst !== null) {
        const bInst = data.inst || null;
        if (myInst !== bInst) {
          visible = false;
        } else if (data.lvl !== undefined && data.lvl !== myLevel) {
          visible = false;
        }
      }
      // Use visibility:hidden (not display:none) — World.js render loop overrides display.
      if (remote.character.sprite?.element) {
        remote.character.sprite.element.style.visibility = visible ? '' : 'hidden';
      }
      if (remote.character.shadow?.element) {
        remote.character.shadow.element.style.visibility = visible ? '' : 'hidden';
      }
    }

    // Switch character model if changed, or if LPC state toggled
    const wasLPC = !!remote.character?.sprite?.getLPCAppearance();
    const isLPC = !!data.lpcA;
    if (remote.character && (data.characterName !== remote.character.characterName || wasLPC !== isLPC)) {
      this._removeRemoteCharacter(peerId);
      remote.character = null;
      remote.config = null;
      remote._lpcEquipmentStr = null;
      await this._createRemoteCharacter(peerId, data);
    }

    // Update LPC appearance or equipment if changed
    if (remote.character && data.lpcA) {
      const currentAppearance = remote.character.sprite?.getLPCAppearance();
      const newEqStr = data.lpcE ? JSON.stringify(data.lpcE) : '';
      const appearanceChanged = !currentAppearance || JSON.stringify(currentAppearance) !== JSON.stringify(data.lpcA);
      const equipmentChanged = (remote._lpcEquipmentStr || '') !== newEqStr;
      if ((appearanceChanged || equipmentChanged) && remote.character.sprite) {
        await loadManifest();
        remote.character.sprite.setLPCAppearance(data.lpcA);
        // Convert lpcE to equippedItems format for buildLayerStack
        const equippedItems = {};
        if (data.lpcE) {
          for (const [slot, lpcLayer] of Object.entries(data.lpcE)) {
            equippedItems[slot] = { lpcLayer };
          }
        }
        remote.character.sprite.updateEquipment(equippedItems);
        remote._lpcEquipmentStr = newEqStr;
      }
    }

    // Retry creation if previous attempt failed (character still null, not currently creating)
    if (!remote.character && !this._creatingPeers.has(peerId)) {
      await this._createRemoteCharacter(peerId, data);
    }
  }

  /**
   * Handle an instant action event from a remote peer.
   */
  _onRemoteAction(data, peerId) {
    if (data.type === 'hello') {
      // Peer is requesting our state — respond so they see us.
      // Throttle to once per second to prevent flooding when multiple peers
      // join simultaneously or retries overlap — each hello triggers a
      // broadcastImmediately from every connected peer, creating O(N²) messages.
      const now = Date.now();
      if (now - this._lastHelloResponse < 1000) return;
      this._lastHelloResponse = now;
      this.broadcastImmediately();
      return;
    }

    // Damage forwarded from authority NPC — applies to LOCAL player, not a remote character.
    // Must be handled before the remote?.character guard since the target is ourselves.
    if (data.type === 'player-damage') {
      if (data.targetPeerId !== this.network.peerId) return;
      const player = this.localPlayer;
      if (!player?.combatStats) return;
      const stats = player.combatStats;
      if (stats.isDead || stats.isInvulnerable) return;

      stats.health -= data.damage;

      // Block health regen for 5 seconds after taking damage
      stats.healthRegenDelay = 5.0;

      // I-frames
      stats.isInvulnerable = true;
      stats.invulnerabilityTimer = stats.invulnerabilityDuration;

      // Knockback from attacker position
      if (data.ax !== undefined && data.az !== undefined) {
        const dx = player.x - data.ax;
        const dz = player.z - data.az;
        const dist = Math.sqrt(dx * dx + dz * dz) || 1;
        const kb = data.knockback || 1.5;
        stats.knockbackVelocity.x = (dx / dist) * kb;
        stats.knockbackVelocity.z = (dz / dist) * kb;
      }

      // Visual feedback
      if (player.sprite) player.sprite._hitFlashTimer = 0.1;

      // Hit reaction or death
      if (stats.health <= 0) {
        stats.health = 0;
        if (window.game?._triggerPlayerDeath) {
          window.game._triggerPlayerDeath();
        }
      } else {
        player.triggerAction('taking-punch');
      }
      return;
    }

    const remote = this.remotePlayers.get(peerId);
    if (!remote?.character) return;

    // Skip actions from peers in a different instance or dungeon level
    const dim = this._dungeonInstanceManager;
    if (dim) {
      const myInst = dim._isInDungeon ? dim._instanceId : null;
      const theirInst = dim._peerInstances.get(peerId) || null;
      if (myInst !== theirInst) return;
      if (myInst !== null) {
        const theirLevel = dim._peerLevels.get(peerId);
        if (theirLevel !== undefined && theirLevel !== dim.currentLevel) return;
      }
    }

    if (data.type === 'anim') {
      // Play one-shot animation (jump, attack, kick, etc.)
      remote.character.triggerAction(data.animName);
    } else if (data.type === 'fireball') {
      // Spawn visual-only fireball on this peer
      this.onRemoteFireball?.(data);
    } else if (data.type === 'boomerang-throw') {
      // Spawn detached boomerang animation on remote character
      this.onRemoteBoomerang?.(remote.character, data.angle);
    }
  }

  /**
   * Broadcast local state immediately (call on peer join to ensure visibility).
   * Uses force=true to bypass idle optimization — peer discovery broadcasts
   * MUST go through even when the player hasn't moved.
   */
  broadcastImmediately() {
    this._broadcastLocalState(true);
    this._broadcastTimer = 0; // Reset timer so next regular broadcast is a full interval away
  }

  /**
   * Called when a new peer joins. Broadcasts our state with staggered retries
   * and sends a single hello handshake to trigger the peer's response.
   * Hello is sent ONCE (not retried) to prevent O(N²) message storms —
   * each hello triggers every peer to respond with broadcastImmediately().
   * State-only retries cover slow mobile WebRTC channel establishment.
   */
  notifyPeerJoined() {
    this.broadcastImmediately();
    this.broadcastAction({ type: 'hello' });

    // Staggered state-only re-broadcasts with force=true (bypass idle optimization).
    // These are critical for peer discovery — the data channel may not be ready
    // when onPeerJoin fires, so retries ensure the peer eventually sees us.
    for (const delay of [500, 2000, 5000]) {
      setTimeout(() => {
        if (this.network.isConnected) {
          this._broadcastLocalState(true);
        }
      }, delay);
    }
  }

  async _createRemoteCharacter(peerId, data) {
    // Guard against concurrent creation (async load can overlap with next position update)
    if (this._creatingPeers.has(peerId)) return;
    this._creatingPeers.add(peerId);

    const hasLPC = !!data.lpcA;
    const characterName = data.characterName || 'boy';
    const config = hasLPC ? LPC_CONFIG : (CHARACTER_CONFIGS[characterName] || CHARACTER_CONFIGS.boy);

    try {
      let atlas, metadata;

      if (hasLPC) {
        // LPC-based character: composite layers from appearance data
        await loadManifest(); // Ensure manifest is loaded (normally loaded by CharacterCreator)
        // Convert broadcast lpcE data to the format buildLayerStack expects
        const equippedItems = {};
        if (data.lpcE) {
          for (const [slot, lpcLayer] of Object.entries(data.lpcE)) {
            equippedItems[slot] = { lpcLayer };
          }
        }
        const layers = buildLayerStack(data.lpcA, equippedItems);
        const trixelAnims = [
          'walking-8-frames', 'breathing-idle', 'running-8-frames',
          // Jump animations
          'jumping-1', 'jumping-2',
          // Standard attack anims (for remote player attack display)
          'lead-jab', 'roundhouse-kick', 'fireball', 'taking-punch', 'falling-back-death',
          // Weapon-specific attack animations
          'weapon-slash', 'weapon-slash-reverse', 'weapon-thrust',
          'weapon-backslash', 'weapon-halfslash', 'weapon-shoot'
        ];
        atlas = await LPCCompositor.compose(layers, trixelAnims);
        // Build a metadata-compatible object for CharacterAnimator.
        // It needs hasAnimation(), getFrameCount(), animationNames, and sprite dimensions.
        const lpcAnimNames = [...atlas.animationLookup.keys()];
        metadata = {
          spriteWidth: 64,
          spriteHeight: 64,
          animationNames: lpcAnimNames,
          hasAnimation(name) { return atlas.animationLookup.has(name); },
          getFrameCount(name, dir) {
            const dm = atlas.animationLookup.get(name);
            if (!dm) return 0;
            const frames = dm.get(dir);
            return frames ? frames.length : 0;
          },
          getSummary() { return `LPC remote (${lpcAnimNames.length} anims)`; }
        };
      } else {
        const assets = await this._loadAssets(characterName);
        atlas = assets.atlas;
        metadata = assets.metadata;
      }

      // Bail if peer was removed while we were loading
      if (!this.remotePlayers.has(peerId)) {
        this._creatingPeers.delete(peerId);
        return;
      }

      const character = new Character({
        id: `remote_${peerId}`,
        x: data.x,
        y: data.y,
        z: data.z
      });
      character.characterName = characterName;
      character.isNPC = false;
      character.isRemote = true;
      character.config = config;

      // Minimal combatStats so HitDetection can detect this character as a valid
      // target for NPC attacks. The authority broadcasts damage to the owning peer.
      character.combatStats = new CombatStats({ maxHealth: 100, attackPower: 0, defense: 0, iFrames: 0.5 });

      // Set character-specific animation names so returnTo (after attacks/jumps)
      // transitions back to the correct idle — without this, the default
      // 'breathing-idle' may not exist in the character's metadata, causing
      // non-looping animations to stick on their last frame permanently.
      character.animations = {
        idle: config.idle,
        walk: config.walk,
        run: config.run
      };

      // Sprite
      const sprite = new CharacterSprite(character, {
        spriteWidth: metadata.spriteWidth,
        spriteHeight: metadata.spriteHeight,
        scale: 2
      });
      if (hasLPC) atlas._isLPC = true;
      sprite.setAtlas(atlas);
      if (hasLPC) sprite.setLPCAppearance(data.lpcA);
      sprite.spriteOffset = { ...(config.spriteOffset || DEFAULT_SPRITE_OFFSET) };
      character.sprite = sprite;

      // Animator
      const animator = new CharacterAnimator(character, metadata);
      animator.setAtlas(atlas);
      character.animator = animator;
      animator.setState(config.idle);
      animator.onDirectionChange('south');

      // Shadow — uses collision system from world for spatial queries
      if (this.world.collisionSystem) {
        character.shadow = new CharacterShadow(character, this.world.collisionSystem);
      }

      // No controller, no pathFollower, no behavior — network-driven
      character.controller = null;

      this.world.addCharacter(character);

      const remote = this.remotePlayers.get(peerId);
      if (remote) {
        remote.character = character;
        remote.config = config;
        // Store equipment hash so subsequent broadcasts don't recomposite unnecessarily
        if (data.lpcE) remote._lpcEquipmentStr = JSON.stringify(data.lpcE);

        // Floating name tag — child of sprite element so it depth-sorts with the world
        const nametagEl = document.createElement('div');
        nametagEl.className = 'player-nametag';
        nametagEl.textContent = ProfanityFilter.filter(remote.nickname || this._titleCase(characterName));
        sprite.element.appendChild(nametagEl);
        remote.nametagEl = nametagEl;

        // Flush pending chat bubble (arrived before character was created)
        if (remote._pendingBubble && (Date.now() - remote._pendingBubble.time < 10000)) {
          const color = this._getPeerColor(peerId);
          this._showBubbleOnSprite(sprite.element, remote._pendingBubble.text, color);
        }
        remote._pendingBubble = null;

        // Apply the latest targetState — asset loading is async and may take
        // seconds on slow mobile devices. During that time, position updates
        // kept arriving and updating targetState. Without this snap, the
        // character appears at the stale position from the first update and
        // must interpolate across the map to catch up, causing a visible teleport.
        if (remote.targetState) {
          character.x = remote.targetState.x;
          character.y = remote.targetState.y;
          character.z = remote.targetState.z;
          character.facingAngle = remote.targetState.angle;
        }

        // Immediately check visibility — character was created visible by default,
        // but the peer may be on a different instance/level. Without this check,
        // the sprite stays visible until the next _onRemoteUpdate (up to 2s).
        const dim = this._dungeonInstanceManager;
        if (dim) {
          const myInst = dim._isInDungeon ? dim._instanceId : null;
          const myLevel = dim._isInDungeon ? dim.currentLevel : undefined;
          const theirInst = dim._peerInstances.get(peerId) || null;
          const theirLevel = dim._peerLevels.get(peerId);
          const bInst = remote.targetState?.inst || null;
          const bLevel = remote.targetState?.lvl;
          let visible = true;
          if (myInst !== theirInst) visible = false;
          else if (myInst !== null && theirLevel !== undefined && theirLevel !== myLevel) visible = false;
          if (visible && myInst !== null) {
            if (myInst !== bInst) visible = false;
            else if (bLevel !== undefined && bLevel !== myLevel) visible = false;
          }
          if (!visible) {
            // Use visibility:hidden (not display:none) — World.js render loop overrides display.
            // Nametag inherits visibility from sprite parent automatically.
            if (character.sprite?.element) character.sprite.element.style.visibility = 'hidden';
            if (character.shadow?.element) character.shadow.element.style.visibility = 'hidden';
          }
        }
      }
    } catch (err) {
      Debug.warn('PlayerSync', `Failed to create remote character for ${peerId}:`, err);
    } finally {
      this._creatingPeers.delete(peerId);
    }
  }

  async _loadAssets(characterName) {
    if (this._atlasCache.has(characterName)) return this._atlasCache.get(characterName);

    const characterPath = `/characters/${characterName}`;
    const metadata = await CharacterMetadata.load(characterPath);
    const atlas = await OptimizedAtlasLoader.load(characterPath, metadata);

    const assets = { metadata, atlas, characterPath };
    this._atlasCache.set(characterName, assets);
    return assets;
  }

  _removeRemoteCharacter(peerId) {
    const remote = this.remotePlayers.get(peerId);
    if (remote?.character) {
      this.world.removeCharacter(remote.character.id);
    }
    if (remote?.nametagEl) {
      remote.nametagEl.remove();
      remote.nametagEl = null;
    }
  }

  /**
   * Remove a specific peer's character (called on peer leave).
   */
  removePeer(peerId) {
    this._removeRemoteCharacter(peerId);
    this.remotePlayers.delete(peerId);
    // #N suffix is permanent for the session — don't revert on peer leave
    // to prevent impersonation of the original player who had that name.
  }

  /**
   * Remove all remote characters (called on disconnect).
   */
  removeAllPeers() {
    for (const peerId of this.remotePlayers.keys()) {
      this._removeRemoteCharacter(peerId);
    }
    this.remotePlayers.clear();
  }

  /**
   * Reset for room transition. Removes all remote players and clears timers.
   * Unlike destroy(), does not null out network callbacks.
   */
  reset() {
    this.removeAllPeers();
    this._broadcastTimer = 0;
    this._lastBroadcastState = null;
    this._lastBroadcastTime = 0;
    this._lastHelloResponse = 0;
    this._roomJoinTime = 0;
    this._lastInstLevelKey = null; // Force visibility re-evaluation after instance transition
  }

  /**
   * Force re-evaluation of remote player visibility based on current instance/level.
   * Called when a remote peer changes instance or level (via dungeon-instance-change).
   * Uses DIM's _peerInstances/_peerLevels as authoritative source since player
   * broadcast targetState may be stale (hasn't arrived yet with new level).
   */
  reevaluateVisibility() {
    const dim = this._dungeonInstanceManager;
    const myInst = dim?._isInDungeon ? dim._instanceId : null;
    const myLevel = dim?._isInDungeon ? dim.currentLevel : undefined;
    for (const [peerId, remote] of this.remotePlayers) {
      if (!remote.character) continue;
      // Use DIM's tracking as authoritative for instance/level
      const theirInst = dim?._peerInstances.get(peerId) || null;
      const theirLevel = dim?._peerLevels.get(peerId);
      const visible = (myInst === theirInst) && (myInst === null || theirLevel === undefined || theirLevel === myLevel);
      // Use visibility:hidden (not display:none) — World.js render loop overrides display.
      if (remote.character.sprite?.element) {
        remote.character.sprite.element.style.visibility = visible ? '' : 'hidden';
      }
      if (remote.character.shadow?.element) {
        remote.character.shadow.element.style.visibility = visible ? '' : 'hidden';
      }
    }
  }

  /**
   * Set local nickname and run dedup against current remote peers.
   * Returns the final display name (with #N suffix if duplicate).
   */
  setLocalNickname(nick) {
    this.localNickname = nick;
    if (!this._roomJoinTime) this._roomJoinTime = Date.now();
    this._recheckLocalDedup();
    return this.localDisplayNick;
  }

  /**
   * Re-check if our local nickname collides with any remote peer's nickname.
   * Only yields (appends #2, #3, etc.) if a remote with the SAME name joined
   * EARLIER (lower join timestamp). The first player in the room keeps the
   * base name; later joiners get #2, #3, etc. Ties broken by peer ID.
   * Once a #N suffix is assigned, it's permanent for the session.
   */
  _recheckLocalDedup() {
    const base = this.localNickname;
    if (!base) {
      this.localDisplayNick = null;
      return;
    }

    // Once we have a #N suffix, it's permanent — don't revert
    if (this.localDisplayNick && this.localDisplayNick.includes('#')) {
      return;
    }

    const lower = base.toLowerCase();
    const myJoinTime = this._roomJoinTime || 0;
    const myId = this.network.peerId;

    // Check if any remote has the same name AND joined before us (has priority)
    let mustYield = false;
    const taken = new Set();
    for (const [peerId, remote] of this.remotePlayers) {
      if (remote.nickname) {
        taken.add(remote.nickname.toLowerCase());
        if (remote.nickname.toLowerCase() === lower) {
          // Earlier join time = priority. If equal (extremely unlikely), larger peer ID wins.
          const theirJoinTime = remote.joinTime || 0;
          if (theirJoinTime < myJoinTime || (theirJoinTime === myJoinTime && peerId > myId)) {
            mustYield = true;
          }
        }
      }
    }

    if (!mustYield) {
      // We have priority (or no collision) — keep base name
      if (this.localDisplayNick !== base) {
        this.localDisplayNick = base;
        this.onDisplayNickChange?.(base);
      }
      return;
    }
    // Must yield — find first unused suffix
    for (let i = 2; i <= 99; i++) {
      const candidate = `${base}#${i}`;
      if (!taken.has(candidate.toLowerCase())) {
        if (this.localDisplayNick !== candidate) {
          this.localDisplayNick = candidate;
          this.onDisplayNickChange?.(candidate);
        }
        return;
      }
    }
    // Fallback (extremely unlikely)
    this.localDisplayNick = base;
  }

  static LOCAL_CHAT_COLOR = '#28b482';
  static CHAT_BUBBLE_DURATION = 5000;

  /**
   * Show a chat bubble above a character's sprite.
   * @param {string|null} peerId - null for local player
   * @param {string} text - message text (already filtered)
   */
  showChatBubble(peerId, text) {
    let spriteEl, color;

    if (peerId === null) {
      spriteEl = this.localPlayer?.sprite?.element;
      color = PlayerSync.LOCAL_CHAT_COLOR;
    } else {
      const remote = this.remotePlayers.get(peerId);
      if (!remote) return;
      spriteEl = remote.character?.sprite?.element;
      color = this._getPeerColor(peerId);

      // Character not created yet (async asset loading) — store for later
      if (!spriteEl) {
        remote._pendingBubble = { text, time: Date.now() };
        return;
      }
    }
    if (!spriteEl) return;

    this._showBubbleOnSprite(spriteEl, text, color);
  }

  /** @private */
  _showBubbleOnSprite(spriteEl, text, color) {
    // Reuse existing bubble or create new one
    let bubble = spriteEl.querySelector('.player-chat-bubble');
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'player-chat-bubble';
      spriteEl.appendChild(bubble);
    }

    bubble.textContent = text;
    bubble.style.borderColor = color;
    bubble.style.opacity = '1';
    bubble.style.display = 'block';

    // Clear existing timers
    if (bubble._fadeTimer) clearTimeout(bubble._fadeTimer);
    if (bubble._removeTimer) clearTimeout(bubble._removeTimer);

    // Fade out after duration, then hide
    bubble._fadeTimer = setTimeout(() => {
      bubble.style.opacity = '0';
      bubble._removeTimer = setTimeout(() => {
        bubble.style.display = 'none';
      }, 500);
    }, PlayerSync.CHAT_BUBBLE_DURATION);
  }

  _getPeerColor(peerId) {
    if (!peerId) return PEER_COLORS[0];
    let hash = 0;
    for (let i = 0; i < peerId.length; i++) {
      hash = ((hash << 5) - hash + peerId.charCodeAt(i)) | 0;
    }
    return PEER_COLORS[Math.abs(hash) % PEER_COLORS.length];
  }

  _titleCase(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  destroy() {
    this.removeAllPeers();
    this.network.onPlayerUpdate = null;
    this.network.onAction = null;
  }
}
